import React, { useRef, useEffect, useCallback, useState } from 'react';

interface DraggablePopularTagProps {
  label: string;
  className?: string;
}

export default function DraggablePopularTag({ label, className = '' }: DraggablePopularTagProps) {
  const [isDragging, setIsDragging] = useState(false);
  const svgRef = useRef<SVGSVGElement>(null);
  const dragStartX = useRef(0);
  const lastDragX = useRef(0);
  const animationRef = useRef<number>();
  const autoSwingTime = useRef(0);
  const isDraggingRef = useRef(false);
  const rotationRef = useRef(0);
  const velocityRef = useRef(0);
  const isAutoSwingingRef = useRef(true);

  const applyRotation = (deg: number) => {
    rotationRef.current = deg;
    if (svgRef.current) {
      svgRef.current.style.transform = `rotate(${deg}deg)`;
    }
  };

  const handleGlobalMouseMove = useCallback((e: MouseEvent) => {
    if (!isDraggingRef.current) return;
    const deltaX = e.clientX - dragStartX.current;
    applyRotation(-deltaX * 0.5);
    velocityRef.current = -(e.clientX - lastDragX.current) * 0.3;
    lastDragX.current = e.clientX;
  }, []);

  const handleGlobalMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
    isAutoSwingingRef.current = false;
    document.removeEventListener('mousemove', handleGlobalMouseMove);
    document.removeEventListener('mouseup', handleGlobalMouseUp);
  }, [handleGlobalMouseMove]);

  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [handleGlobalMouseMove, handleGlobalMouseUp]);

  useEffect(() => {
    const animate = () => {
      if (!isDraggingRef.current) {
        if (isAutoSwingingRef.current) {
          autoSwingTime.current += 0.02;
          applyRotation(Math.sin(autoSwingTime.current * 1.2) * 8);
        } else {
          const springForce = -rotationRef.current * 0.005;
          const dampingForce = -velocityRef.current * 0.02;
          const newVelocity = velocityRef.current + springForce + dampingForce;
          const newRotation = rotationRef.current + newVelocity;

          if (Math.abs(newRotation) < 0.3 && Math.abs(newVelocity) < 0.3) {
            velocityRef.current = 0;
            isAutoSwingingRef.current = true;
            applyRotation(0);
          } else {
            velocityRef.current = newVelocity;
            applyRotation(newRotation);
          }
        }
      }
      animationRef.current = requestAnimationFrame(animate);
    };

    animationRef.current = requestAnimationFrame(animate);
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    isDraggingRef.current = true;
    setIsDragging(true);
    isAutoSwingingRef.current = false;
    dragStartX.current = e.clientX;
    lastDragX.current = e.clientX;
    velocityRef.current = 0;
    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
  };

  return (
    <div
      className={`absolute z-50 cursor-grab active:cursor-grabbing select-none ${className}`}
      onMouseDown={handleMouseDown}
    >
      <div className="relative w-3.5 h-3.5 rounded-full bg-gradient-to-br from-gray-300 via-gray-400 to-gray-600 shadow-[0_2px_4px_rgba(0,0,0,0.3),inset_0_1px_1px_rgba(255,255,255,0.8)] border border-gray-700/20">
        <div className="absolute top-0.5 left-0.5 w-0.5 h-0.5 bg-white/40 rounded-full blur-[0.5px]"></div>
        <svg
          ref={svgRef}
          className="absolute top-0 w-32 h-44 overflow-visible"
          viewBox="0 0 100 120"
          style={{
            left: '-57px',
            transformOrigin: '64px 0px',
            transform: 'rotate(0deg)',
          }}
        >
          <path
            d="M 50,0 C 32,0 32,45 50,45 C 68,45 68,0 50,0"
            fill="none"
            stroke="#5c4d47"
            strokeWidth="1.2"
            strokeLinecap="round"
            strokeOpacity="0.45"
          />
          <foreignObject x="5" y="38" width="90" height="55" className="overflow-visible">
            <div className={`relative w-[90px] px-2 py-3 rounded-lg shadow-[0_8px_16px_-6px_rgba(0,0,0,0.15)] border border-surface-border overflow-hidden transform rotate-3 bg-surface-light ${isDragging ? 'ring-2 ring-brand-500/30' : ''}`}>
              <div className="absolute inset-0 bg-noise opacity-[0.05] pointer-events-none"></div>
              <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-2.5 h-2.5 rounded-full bg-surface shadow-[inset_0_1px_2px_rgba(0,0,0,0.08)] border border-surface-border flex items-center justify-center">
                <div className="w-1 h-1 rounded-full bg-brand-900/10"></div>
              </div>
              <div className="mt-1.5 flex flex-col items-center">
                <span className="text-brand-700 font-serif italic text-sm font-black leading-none tracking-tight whitespace-nowrap">{label}</span>
                <div className="mt-1 w-4 h-[1px] bg-brand-700/15"></div>
              </div>
            </div>
          </foreignObject>
        </svg>
      </div>
    </div>
  );
}
